import { useState } from "react";
import { ActionModal, FIELD_LABEL_CLASS, INPUT_CLASS } from "./ActionModal";

/** The blocked-on note — the mechanism that resets the 7-day reminder
 *  clock, so it must be one click. */
export function NoteModal({
  title = "Log a blocked-on note",
  helper,
  problemTitle,
  standing = null,
  resetsReminder = true,
  onSubmit,
  onClose,
  pending,
}: {
  title?: string;
  /** One plain line, for callers writing an engine-scoped note (the Engines
   *  page) where the incident framing below does not apply. */
  helper?: string;
  /** Set when the note is scoped to one problem on the plan — it groups under
   *  that problem in the blocked-on log, so the writer should be told which
   *  one they are answering before they write it. */
  problemTitle?: string;
  /** The wording that already stands, when one does: the modal opens ON it
   *  rather than on a blank, because the writer is almost always amending
   *  what is there rather than composing from nothing. */
  standing?: string | null;
  /** False for a note that moves no incident's clock (the engine note) — the
   *  reminder line below would be a promise this modal cannot keep. */
  resetsReminder?: boolean;
  onSubmit: (note: string) => void;
  onClose: () => void;
  pending?: boolean;
}) {
  const [note, setNote] = useState(standing ?? "");

  /* A purple "this incident is engine-linked" callout stood here until
     2026-08-14 (backend ADR 0022). It told the writer their note was going on
     a shared engine investigation rather than on this hotel, that every linked
     hotel would show it, and that one entry reset all of their reminder
     clocks — so write it about the engine, not the property. None of that is
     true any more: the note lands on this incident and moves this hotel's
     clock. Do not reintroduce the guidance without the shared row behind it. */

  const trimmed = note.trim();

  /**
   * Saving the standing wording back unchanged is not an act, so the button
   * does not offer it: it would add an entry to the log and buy another week
   * of quiet on a hotel where nobody did anything. Only new wording is worth
   * a new entry, and only a new entry moves the clock. With nothing standing
   * the comparison is against the empty string, which the blank check already
   * covers.
   */
  const unchanged = trimmed === (standing?.trim() ?? "");

  return (
    <ActionModal
      title={title}
      submitLabel={standing ? "Save note" : "Log note"}
      onSubmit={() => onSubmit(trimmed)}
      onClose={onClose}
      pending={pending}
      disabled={!trimmed || unchanged}
    >
      {helper && <p className="text-sm text-gray-500">{helper}</p>}
      {problemTitle && (
        <p className="text-sm text-gray-500">
          Against{" "}
          <span className="font-medium text-content-primary">
            {problemTitle}
          </span>
          . It groups under that problem in the blocked-on log — it does not
          resolve it, and the fix log is what does.
        </p>
      )}
      <div>
        <label className={FIELD_LABEL_CLASS}>Note</label>
        <textarea
          className={`${INPUT_CLASS} min-h-[88px]`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What is this blocked on, and who was chased?"
          autoFocus
        />
      </div>
      {resetsReminder && (
        <p className="text-xs text-gray-400">
          The 7-day reminder is what chases an open incident in Slack once a
          week has passed with nothing new written on it. It counts from the
          latest note, so logging one starts the seven days again.
        </p>
      )}
    </ActionModal>
  );
}
